/** 備品マスタ。1行=1個体。管理番号で個体を追い、誰に貸しているかは貸与品台帳と同じ情報源。 */
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Drawer,
  Flex,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import type { Equipment, EquipmentInput, EquipmentPatch, Loan } from "../../lib/api";
import { useApi, useMe } from "../../lib/useApi";
import { hasFeature } from "../../lib/features";
import { hasRole } from "../../lib/roles";
import { jst } from "../../lib/format";
import { useDebounced } from "../../lib/useDebounced";
import {
  EQUIPMENT_STATUSES,
  equipmentOwnershipLabel,
  equipmentStatusColor,
  equipmentStatusLabel,
} from "./constants";
import { errorText, useHrPerms } from "./hooks";
import { useEquipment, useEquipmentCategories, useInvalidateLoans, useLoans } from "./loanHooks";

/** 直接貸出の相手。在籍中の人だけ(サーバー側も active しか受け付けない)。
 *
 * 検索も**サーバーに投げる**。手元の 200 件だけで絞ると、201 人目以降は
 * 名前を打っても出てこない。
 */
function useActiveEmployees(enabled: boolean, term: string) {
  const api = useApi();
  return useQuery({
    queryKey: ["hr", "employees", "active-for-lend", term],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ limit: "200", status: "active" });
      if (term) params.set("q", term);
      return api.employees(params, signal);
    },
    enabled,
    staleTime: 30_000,
  });
}

/** 紐づいた MDM の端末。製造番号一致で自動(device.seen)、または「端末を選ぶ」で手動。
 *
 * 手動(manual)で付けた端末は製造番号が違っても自動では外れない。製造番号の食い違いは
 * 警告を出すだけで書き換えない。端末詳細へのリンクと選択肢は mdm:viewer があって、
 * MDM の契約機能(mdm.apple / mdm.windows / mdm.android のどれか)が有効な人だけ。
 */
const PICK_LIMIT = 50;
function LinkedDevice({ eq, canWrite }: { eq: Equipment; canWrite: boolean }) {
  const api = useApi();
  const me = useMe();
  const invalidate = useInvalidateLoans();
  const deviceId = eq.device_id;
  // mdm:viewer が無い(または MDM の契約が無い)人が引くと 403 になるだけなので、id だけ出す
  const canView =
    hasRole(me.data, "mdm:viewer") && hasFeature(me.data, ["mdm.apple", "mdm.windows", "mdm.android"]);
  const [picking, setPicking] = useState(false);
  const [search, setSearch] = useState("");
  const [pick, setPick] = useState<number | undefined>();
  const term = useDebounced(search);
  const query = useQuery({
    queryKey: ["mdm", "device", deviceId],
    queryFn: () => api.device(deviceId!),
    enabled: !!deviceId && canView,
  });
  const devices = useQuery({
    queryKey: ["mdm", "devices", "pick-for-equipment", term],
    queryFn: () =>
      api.devices(new URLSearchParams({ limit: String(PICK_LIMIT), ...(term ? { q: term } : {}) })),
    enabled: picking,
  });
  const save = useMutation({
    mutationFn: (v: number | null) => api.setEquipmentDevice(eq.id, v),
    onSuccess: (_, v) => {
      void message.success(v == null ? "端末の紐づけを外した" : "端末を紐づけた");
      invalidate();
      setPicking(false);
      setPick(undefined);
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const d = query.data;
  const mismatch = !!d && !!d.serial && !!eq.serial && d.serial !== eq.serial;
  return (
    <Flex gap={8} wrap align="center">
      {!deviceId ? (
        <span>未紐づけ</span>
      ) : !d ? (
        <span>#{deviceId}</span>
      ) : (
        <span>
          <Link to={`/mdm/devices/${d.id}`}>{d.hostname}</Link>{" "}
          <Typography.Text type="secondary">
            {d.serial || "製造番号なし"} / 最終チェックイン {jst(d.last_seen)}
          </Typography.Text>
        </span>
      )}
      {deviceId && eq.device_link_mode === "manual" && <Tag>手動</Tag>}
      {mismatch && (
        <Typography.Text type="warning">製造番号が端末({d.serial})と違う</Typography.Text>
      )}
      {canWrite && canView && (
        <Button size="small" onClick={() => setPicking(true)}>
          端末を選ぶ
        </Button>
      )}
      {canWrite && deviceId && (
        <Popconfirm
          title="端末の紐づけを外す"
          description="外すと自動(製造番号一致)に戻る"
          okText="外す"
          cancelText="やめる"
          onConfirm={() => save.mutate(null)}
        >
          <Button size="small" danger loading={save.isPending}>
            外す
          </Button>
        </Popconfirm>
      )}
      <Modal
        title="MDM の端末を選ぶ"
        open={picking}
        okText="紐づける"
        cancelText="やめる"
        confirmLoading={save.isPending}
        onCancel={() => setPicking(false)}
        onOk={() => {
          if (!pick) {
            void message.error("端末を選ぶ");
            return;
          }
          save.mutate(pick);
        }}
        destroyOnClose
      >
        <Select
          style={{ width: "100%" }}
          placeholder="ホスト名・製造番号・利用者で検索"
          showSearch
          filterOption={false}
          value={pick}
          loading={devices.isFetching}
          onSearch={setSearch}
          onChange={(v: number) => setPick(v)}
          options={(devices.data?.items ?? []).map((x) => ({
            value: x.id,
            label: `${x.hostname}(${x.serial || "製造番号なし"} / ${x.model || "機種不明"} / ${
              x.employee_name || x.employee_email || "利用者なし"
            })`,
          }))}
        />
        {devices.isError && (
          <Alert type="error" showIcon style={{ marginTop: 8 }} message={errorText(devices.error)} />
        )}
        {devices.data && devices.data.total > devices.data.items.length && (
          <Typography.Paragraph type="warning" style={{ marginTop: 8, marginBottom: 0 }}>
            {devices.data.total} 台のうち先頭 {devices.data.items.length} 台だけ表示している。
            ホスト名・製造番号で絞り込む
          </Typography.Paragraph>
        )}
        <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
          手で選んだ端末は、製造番号が違ってもチェックインで外れない。外すと自動(製造番号一致)に戻る
        </Typography.Paragraph>
        <Typography.Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 0 }}>
          利用者が片側だけなら、もう片側へ転記する(端末の利用者 → 貸与先 / 貸与先 → 端末の利用者)
        </Typography.Paragraph>
      </Modal>
    </Flex>
  );
}

/** フォームの値(日付は dayjs)。API に送るときに文字列へ直す。 */
type EquipmentFormValues = Omit<EquipmentInput, "rental_start" | "rental_end"> & {
  rental_start?: dayjs.Dayjs | null;
  rental_end?: dayjs.Dayjs | null;
};

function dateOut<B extends "" | null>(v: dayjs.Dayjs | null | undefined, blank: B): string | B {
  return v ? v.format("YYYY-MM-DD") : blank;
}

/** 機器台帳の列(メーカー・色・所有区分・レンタル契約・電話番号・用途)。登録と更新で共通。 */
function LedgerFields({
  ownerships,
  showUnlock,
  unlockExtra,
}: {
  ownerships: string[];
  showUnlock: boolean;
  unlockExtra?: string;
}) {
  return (
    <>
      <Flex gap={8}>
        <Form.Item name="maker" label="メーカー" style={{ flex: 1 }}>
          <Input maxLength={100} />
        </Form.Item>
        <Form.Item name="name" label="機種名" style={{ flex: 2 }}>
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item name="color" label="色" style={{ flex: 1 }}>
          <Input maxLength={50} />
        </Form.Item>
      </Flex>
      <Form.Item name="serial" label="製造番号" extra="MDM の端末と突き合わせるのに使う">
        <Input maxLength={200} />
      </Form.Item>
      <Flex gap={8}>
        <Form.Item name="ownership" label="所有区分" style={{ width: 140 }}>
          <Select options={ownerships.map((o) => ({ value: o, label: equipmentOwnershipLabel(o) }))} />
        </Form.Item>
        <Form.Item name="rental_vendor" label="レンタル業者" style={{ flex: 1 }}>
          <Input maxLength={100} />
        </Form.Item>
        <Form.Item name="rental_start" label="契約開始">
          <DatePicker style={{ width: 140 }} />
        </Form.Item>
        <Form.Item name="rental_end" label="契約終了">
          <DatePicker style={{ width: 140 }} />
        </Form.Item>
      </Flex>
      <Flex gap={8}>
        <Form.Item name="phone_number" label="電話番号" style={{ width: 180 }}>
          <Input maxLength={30} />
        </Form.Item>
        <Form.Item
          name="purpose"
          label="用途"
          style={{ flex: 1 }}
          extra="人ではなく役割で使う端末(管理端末・踏み台など)はここに書く"
        >
          <Input maxLength={200} />
        </Form.Item>
      </Flex>
      {showUnlock && (
        <Form.Item name="unlock_code" label="ロック解除番号" extra={unlockExtra}>
          <Input maxLength={100} />
        </Form.Item>
      )}
    </>
  );
}

/** 備品を登録する。区分と管理番号は後から変えられないので、ここだけで決める。 */
function EquipmentFormModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const api = useApi();
  const invalidate = useInvalidateLoans();
  const cats = useEquipmentCategories();
  const [form] = Form.useForm<EquipmentFormValues>();

  const create = useMutation({
    mutationFn: (v: EquipmentFormValues) =>
      api.createEquipment({
        ...v,
        rental_start: dateOut(v.rental_start, null),
        rental_end: dateOut(v.rental_end, null),
      }),
    onSuccess: (eq) => {
      void message.success(`備品 ${eq.asset_no} を登録した`);
      form.resetFields();
      invalidate();
      onClose();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  return (
    <Modal
      open={open}
      title="備品を登録"
      width={720}
      okText="登録"
      cancelText="やめる"
      confirmLoading={create.isPending}
      onCancel={onClose}
      onOk={() => {
        void form.validateFields().then((v) => create.mutate(v));
      }}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        style={{ marginTop: 16 }}
        initialValues={{ status: "available", ownership: "owned" }}
      >
        <Form.Item
          name="category"
          label="区分"
          extra="PC・携帯・入館カードは申請の品目と同じ。マウスなどは台帳では「他貸与品」として載る"
          rules={[{ required: true, message: "区分を選ぶ" }]}
        >
          <Select
            options={(cats.data?.categories ?? []).map((c) => ({ value: c, label: c }))}
            loading={cats.isFetching}
          />
        </Form.Item>
        <Form.Item
          name="asset_no"
          label="管理番号"
          extra="社内で一意。登録したら変えられない"
          rules={[{ required: true, message: "管理番号を入れる" }]}
        >
          <Input maxLength={100} />
        </Form.Item>
        <LedgerFields
          ownerships={cats.data?.ownerships ?? []}
          showUnlock
          unlockExtra="保存後は hr:admin だけが見られる"
        />
        <Form.Item name="status" label="状態" extra="貸与中は貸出の結果として付くので選べない">
          <Select
            options={(cats.data?.statuses ?? []).map((s) => ({
              value: s,
              label: equipmentStatusLabel(s),
            }))}
          />
        </Form.Item>
        <Form.Item name="note" label="備考">
          <Input maxLength={500} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

/** 個体の明細。更新・即時貸出・即時返却と貸与履歴。 */
function EquipmentDrawer({ id, onClose }: { id: number | null; onClose: () => void }) {
  const api = useApi();
  const invalidate = useInvalidateLoans();
  const { canWrite, canAdmin } = useHrPerms();
  const cats = useEquipmentCategories();
  const [form] = Form.useForm<EquipmentFormValues>();
  const [lendTo, setLendTo] = useState<number | undefined>();
  const [lendNote, setLendNote] = useState("");
  const [lendAt, setLendAt] = useState<dayjs.Dayjs | null>(null);
  const [lendSearch, setLendSearch] = useState("");

  const query = useQuery({
    queryKey: ["hr", "equipment-item", id],
    queryFn: () => api.equipmentItem(id!),
    enabled: !!id,
  });
  const eq = query.data;
  const history = useLoans({ equipmentId: id ?? undefined, enabled: !!id });
  const lendTerm = useDebounced(lendSearch);
  const employees = useActiveEmployees(!!id && eq?.status === "available" && canWrite, lendTerm);

  useEffect(() => {
    if (eq) {
      form.setFieldsValue({
        name: eq.name,
        serial: eq.serial,
        status: eq.status,
        note: eq.note,
        maker: eq.maker,
        color: eq.color,
        ownership: eq.ownership,
        rental_vendor: eq.rental_vendor,
        rental_start: eq.rental_start ? dayjs(eq.rental_start) : null,
        rental_end: eq.rental_end ? dayjs(eq.rental_end) : null,
        phone_number: eq.phone_number,
        purpose: eq.purpose,
        // hr:admin 以外には空で返るので、空のまま送って消してしまわないよう admin だけ載せる
        ...(canAdmin ? { unlock_code: eq.unlock_code } : {}),
      });
    }
    setLendTo(undefined);
    setLendNote("");
    setLendAt(null);
    setLendSearch("");
  }, [eq, form, canAdmin]);

  const save = useMutation({
    mutationFn: (v: EquipmentFormValues) => {
      const body: EquipmentPatch = {
        ...v,
        rental_start: dateOut(v.rental_start, ""),
        rental_end: dateOut(v.rental_end, ""),
      };
      delete (body as { category?: string }).category;
      delete (body as { asset_no?: string }).asset_no;
      return api.updateEquipment(id!, body);
    },
    onSuccess: () => {
      void message.success("備品を更新した");
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const lend = useMutation({
    mutationFn: () =>
      api.lendEquipment(id!, {
        employee_id: lendTo!,
        note: lendNote,
        ...(lendAt ? { lent_at: lendAt.format("YYYY-MM-DD") } : {}),
      }),
    onSuccess: (res) => {
      void message.success(`${res.asset_no} の貸与を記録した`);
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const back = useMutation({
    mutationFn: () => api.returnEquipment(id!),
    onSuccess: (res) => {
      void message.success(`${res.asset_no} の返却を記録した`);
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const columns: ColumnsType<Loan> = [
    {
      title: "対象者",
      width: 280,
      render: (_: unknown, row) => (
        <span>
          <Link to={`/hr/employees/${row.employee_id}`}>{row.employee_name}</Link>{" "}
          <Typography.Text type="secondary">{row.emp_code}</Typography.Text>
        </span>
      ),
    },
    { title: "品目", dataIndex: "item", width: 160 },
    { title: "貸与日", width: 180, render: (_: unknown, row) => jst(row.lent_at) },
    {
      title: "返却日",
      width: 180,
      render: (_: unknown, row) =>
        row.returned_at ? jst(row.returned_at) : <Tag color="gold">貸与中</Tag>,
    },
  ];

  return (
    <Drawer
      open={!!id}
      width={720}
      onClose={onClose}
      title={eq ? `${eq.asset_no}(${eq.category} / ${eq.name || "機種名なし"})` : "備品"}
      destroyOnClose
    >
      {query.isError && (
        <Alert
          type="error"
          showIcon
          message="備品を取得できなかった"
          description={errorText(query.error)}
        />
      )}
      {eq && (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label="状態">
              <Tag color={equipmentStatusColor(eq.status)}>{equipmentStatusLabel(eq.status)}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="メーカー / 機種名">
              {[eq.maker, eq.name].filter(Boolean).join(" ") || "—"}
              {eq.color && <Typography.Text type="secondary">({eq.color})</Typography.Text>}
            </Descriptions.Item>
            <Descriptions.Item label="製造番号">{eq.serial || "—"}</Descriptions.Item>
            <Descriptions.Item label="所有区分">
              {equipmentOwnershipLabel(eq.ownership)}
              {eq.ownership === "rental" && (
                <Typography.Text type="secondary">
                  {" "}
                  {eq.rental_vendor || "業者未記入"} / {eq.rental_start ?? "?"} 〜 {eq.rental_end ?? "?"}
                </Typography.Text>
              )}
            </Descriptions.Item>
            {eq.phone_number && (
              <Descriptions.Item label="電話番号">{eq.phone_number}</Descriptions.Item>
            )}
            {eq.purpose && <Descriptions.Item label="用途">{eq.purpose}</Descriptions.Item>}
            {canAdmin && eq.unlock_code && (
              <Descriptions.Item label="ロック解除番号">
                <Typography.Text copyable>{eq.unlock_code}</Typography.Text>{" "}
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  hr:admin だけに表示
                </Typography.Text>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="貸与先">
              {eq.holder ? (
                <span>
                  <Link to={`/hr/employees/${eq.holder.employee_id}`}>
                    {eq.holder.employee_name}
                  </Link>{" "}
                  <Typography.Text type="secondary">
                    {eq.holder.employee_email} / {jst(eq.holder.lent_at)} から
                  </Typography.Text>
                </span>
              ) : (
                "貸与なし"
              )}
            </Descriptions.Item>
            <Descriptions.Item label="MDM の端末">
              <LinkedDevice eq={eq} canWrite={canWrite} />
            </Descriptions.Item>
            <Descriptions.Item label="備考">{eq.note || "—"}</Descriptions.Item>
          </Descriptions>

          {canWrite && eq.status === "available" && (
            <Card size="small" title="貸し出す(直接貸出)">
              <Flex gap={8} wrap align="center">
                <Select
                  style={{ width: 280 }}
                  placeholder="貸与先(在籍中の人だけ)"
                  showSearch
                  // サーバーで絞ったものをそのまま出す(手元の 200 件だけで絞らない)
                  filterOption={false}
                  onSearch={setLendSearch}
                  value={lendTo}
                  loading={employees.isFetching}
                  onChange={(v: number) => setLendTo(v)}
                  options={(employees.data?.items ?? []).map((e) => ({
                    value: e.id,
                    label: `${e.name}(${e.emp_code})`,
                  }))}
                />
                <DatePicker
                  style={{ width: 150 }}
                  placeholder="貸与日(今日)"
                  value={lendAt}
                  onChange={(v) => setLendAt(v)}
                  disabledDate={(d) => d.isAfter(dayjs(), "day")}
                />
                <Input
                  style={{ width: 200 }}
                  placeholder="メモ"
                  value={lendNote}
                  maxLength={500}
                  onChange={(e) => setLendNote(e.target.value)}
                />
                <Button
                  type="primary"
                  loading={lend.isPending}
                  onClick={() => {
                    if (!lendTo) {
                      void message.error("貸与先を選ぶ");
                      return;
                    }
                    lend.mutate();
                  }}
                >
                  貸与を記録
                </Button>
              </Flex>
            </Card>
          )}

          {canWrite && eq.status === "lent" && (
            <Card size="small" title="返却">
              <Space>
                <Popconfirm
                  title="この個体の返却を記録する"
                  okText="記録する"
                  cancelText="やめる"
                  onConfirm={() => back.mutate()}
                >
                  <Button danger loading={back.isPending}>
                    返却を記録
                  </Button>
                </Popconfirm>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  申請(変更・退場)の返却タスクを完了しても同じ台帳行が閉じる
                </Typography.Text>
              </Space>
            </Card>
          )}

          {canWrite && (
            <Card size="small" title="備品を更新">
              <Form
                form={form}
                layout="vertical"
                onFinish={(v) => save.mutate(v)}
                disabled={save.isPending}
              >
                <LedgerFields
                  ownerships={cats.data?.ownerships ?? []}
                  showUnlock={canAdmin}
                  unlockExtra="変更は監査に残るが値は書かれない"
                />
                <Form.Item
                  name="status"
                  label="状態"
                  extra="貸与中は変えられない(先に返却する)"
                >
                  <Select
                    disabled={eq.status === "lent"}
                    options={(cats.data?.statuses ?? []).map((s) => ({
                      value: s,
                      label: equipmentStatusLabel(s),
                    }))}
                  />
                </Form.Item>
                <Form.Item name="note" label="備考">
                  <Input maxLength={500} />
                </Form.Item>
                <Button type="primary" htmlType="submit" loading={save.isPending}>
                  更新
                </Button>
              </Form>
            </Card>
          )}

          <Card size="small" title="貸与履歴">
            <Table<Loan>
              scroll={{ x: "max-content" }}
              rowKey="id"
              size="small"
              columns={columns}
              dataSource={history.data?.items ?? []}
              loading={history.isFetching}
              pagination={false}
              locale={{ emptyText: "貸与履歴なし" }}
            />
          </Card>
        </Space>
      )}
    </Drawer>
  );
}

export function EquipmentPage() {
  const { canWrite } = useHrPerms();
  const cats = useEquipmentCategories();
  // 台帳の管理番号リンクから `?q=` で飛んでくる
  const [params, setParams] = useSearchParams();
  const [qInput, setQInput] = useState(params.get("q") ?? "");
  const [filters, setFilters] = useState({
    q: params.get("q") ?? "",
    category: "",
    status: "",
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [creating, setCreating] = useState(false);
  // 端末詳細(mdm)の「台帳の備品」から `?id=` で明細を開いて飛んでくる
  const [openId, setOpenId] = useState<number | null>(() => Number(params.get("id")) || null);

  const query = useEquipment({
    ...filters,
    limit: pageSize,
    offset: (page - 1) * pageSize,
  });

  const apply = (next: Partial<typeof filters>) => {
    setPage(1);
    setFilters((f) => ({ ...f, ...next }));
  };

  const columns: ColumnsType<Equipment> = [
    {
      title: "管理番号",
      dataIndex: "asset_no",
      width: 160,
      render: (v: string, row) => <a onClick={() => setOpenId(row.id)}>{v}</a>,
    },
    { title: "区分", dataIndex: "category", width: 160 },
    {
      title: "メーカー / 機種名",
      width: 360,
      render: (_: unknown, row) => (
        <span>
          {[row.maker, row.name].filter(Boolean).join(" ") || "—"}
          {row.purpose && (
            <Typography.Text type="secondary" style={{ marginLeft: 6 }}>
              {row.purpose}
            </Typography.Text>
          )}
        </span>
      ),
    },
    { title: "製造番号", dataIndex: "serial", width: 160, render: (v: string) => v || "—" },
    {
      title: "所有",
      dataIndex: "ownership",
      width: 110,
      render: (v: string, row) =>
        v === "rental" ? (
          <Tag color="blue" title={`${row.rental_start ?? "?"} 〜 ${row.rental_end ?? "?"}`}>
            レンタル
          </Tag>
        ) : (
          equipmentOwnershipLabel(v)
        ),
    },
    {
      title: "状態",
      dataIndex: "status",
      width: 110,
      render: (v: string) => <Tag color={equipmentStatusColor(v)}>{equipmentStatusLabel(v)}</Tag>,
    },
    {
      title: "貸与先",
      width: 400,
      render: (_: unknown, row) =>
        row.holder ? (
          <span>
            <Link to={`/hr/employees/${row.holder.employee_id}`}>{row.holder.employee_name}</Link>{" "}
            <Typography.Text type="secondary">{jst(row.holder.lent_at)}</Typography.Text>
          </span>
        ) : (
          "—"
        ),
    },
    {
      title: "",
      width: 90,
      render: (_: unknown, row) => (
        <Button size="small" onClick={() => setOpenId(row.id)}>
          開く
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Flex align="center" justify="space-between" style={{ marginBottom: 16 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          備品マスタ
        </Typography.Title>
        {canWrite && (
          <Button type="primary" onClick={() => setCreating(true)}>
            備品を登録
          </Button>
        )}
      </Flex>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Flex gap={8} wrap align="center">
          <Input
            placeholder="管理番号 / 機種名 / 製造番号 / メーカー / 電話番号 / 用途"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            onPressEnter={() => apply({ q: qInput })}
            style={{ width: 280 }}
            allowClear
          />
          <Select
            style={{ width: 160 }}
            value={filters.category}
            onChange={(v) => apply({ category: v })}
            options={[
              { value: "", label: "区分(すべて)" },
              ...(cats.data?.categories ?? []).map((c) => ({ value: c, label: c })),
            ]}
          />
          <Select
            style={{ width: 160 }}
            value={filters.status}
            onChange={(v) => apply({ status: v })}
            options={[
              { value: "", label: "状態(すべて)" },
              ...EQUIPMENT_STATUSES.map((s) => ({ value: s.value, label: s.label })),
            ]}
          />
          <Space>
            <Button type="primary" onClick={() => apply({ q: qInput })}>
              絞り込む
            </Button>
            <Button
              onClick={() => {
                setQInput("");
                setParams(new URLSearchParams());
                setPage(1);
                setFilters({ q: "", category: "", status: "" });
              }}
            >
              解除
            </Button>
          </Space>
        </Flex>
      </Card>

      {query.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="備品を取得できなかった"
          description={errorText(query.error)}
        />
      )}

      <Table<Equipment>
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={query.data?.items ?? []}
        loading={query.isFetching}
        scroll={{ x: 1550 }}
        pagination={{
          current: page,
          pageSize,
          total: query.data?.total ?? 0,
          showSizeChanger: true,
          pageSizeOptions: [20, 50, 100, 200],
          showTotal: (total) => `全 ${total} 件`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
        locale={{ emptyText: "該当なし" }}
      />

      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 12 }}>
        1行=1個体。区分は貸与品の品目と同じで、
        <Link to="/hr/loans">貸与品台帳</Link>の品目と突き合わせる。個体を誰に渡したかはタスク完了時に選ぶ
        (申請では品目だけ選ぶ)。備品は SaaS のカタログには混ぜない。
      </Typography.Paragraph>

      <EquipmentFormModal open={creating} onClose={() => setCreating(false)} />
      <EquipmentDrawer id={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}
